import { Box, styled } from '@mui/material';
import { DataGridPro, DataGridProProps } from '@mui/x-data-grid-pro';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GeneralErrorUI } from '../errors/generalError';
import { GridToolbar } from './components/toolbar';
import { useDataGridContext } from './data-grid-context';
import type { ExtendedDataGridProps } from './types';
import { useDataGridColumns } from './use-data-grid-columns';

export const StyledDataGridPro = styled(DataGridPro)<DataGridProProps>(
  ({ theme, rowCount, error }) => ({
    '& .MuiContainer-root': {
      margin: error ? '0' : 'initial',
    },
    '& .MuiDataGrid-row': {
      cursor: 'pointer',
    },
    '& .MuiDataGrid-cell': {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
    '&&& .MuiDataGrid-cellContent': {
      padding: 0,
    },
    '& .MuiDataGrid-columnSeparator': {
      display: 'none',
    },
    '& .MuiDataGrid-overlay': {
      pointerEvents: rowCount ? ' auto' : 'none',
    },
    '&&& .MuiDataGrid-columnHeader': {
      padding: 0,
    },
    '& .MuiDataGrid-columnHeaderTitleContainerContent': {
      flexGrow: 1,
      height: '100%',
      alignItems: 'flex-start',

      '& .MuiDataGrid-checkboxInput': {
        marginLeft: theme.spacing(2),
        alignSelf: 'center',
      },
    },
    '& .MuiDataGrid-toolbarContainer': {
      paddingTop: 0,
      paddingBottom: theme.spacing(4),
    },
  }),
);

const GRID_HEIGHT = 575;
const HEADER_HEIGHT = 42;
const HEADER_HEIGHT_WITH_FILTER = 92;
const FOOTER_HEIGHT = 69;
const TOOLBAR_HEIGHT = 72;

const AbsoluteBox = styled(Box)(({ hasFilters }: { hasFilters: boolean }) => ({
  position: 'absolute',
  top: hasFilters
    ? HEADER_HEIGHT_WITH_FILTER + TOOLBAR_HEIGHT
    : HEADER_HEIGHT + TOOLBAR_HEIGHT,
  left: 0,
  right: 0,
  bottom: FOOTER_HEIGHT,
  zIndex: 1000,
}));

export function StyledDataGrid(
  props: ExtendedDataGridProps,
): React.JSX.Element {
  const {
    components,
    componentsProps,
    autoHeight: autoHeightProp,
    columns: columnsProp,
    rows,
    error,
    ...rest
  } = props;

  const { t } = useTranslation('table');

  const columns = useDataGridColumns(columnsProp);
  const context = useDataGridContext();

  const [autoHeight, setAutoHeight] = useState(true);

  const handlePageSizeChange = (pageSize: number): void =>
    context.setPaginationModel((prevState) => ({
      ...prevState,
      pageSize,
    }));

  const handlePageChange = (currentPage: number): void =>
    context.setPaginationModel((prevState) => ({
      ...prevState,
      currentPage,
    }));

  useEffect(() => {
    setAutoHeight(Boolean(rows.length));
  }, [rows]);

  const hasFilters = columns.some((col) => !!col.renderHeaderFilter);

  return (
    <Box
      height={autoHeight ? undefined : `${GRID_HEIGHT}px`}
      sx={{ position: 'relative' }}
    >
      <StyledDataGridPro
        autoHeight={autoHeightProp ?? autoHeight}
        headerHeight={hasFilters ? HEADER_HEIGHT_WITH_FILTER : HEADER_HEIGHT}
        filterMode={rest.treeData ? undefined : 'server'}
        sortingMode='server'
        disableColumnSelector
        disableColumnFilter
        columns={columns}
        // Component-related props
        components={{
          ...components,
          Toolbar: components?.Toolbar ?? GridToolbar,
          NoResultsOverlay:
            components?.NoResultsOverlay ?? components?.NoRowsOverlay,
        }}
        componentsProps={{
          ...componentsProps,
          toolbar: { columns, ...componentsProps?.toolbar },
          pagination: {
            labelRowsPerPage: t('pagination.rowsPerPage'),
            labelDisplayedRows: ({
              from,
              to,
              count,
            }: {
              from: number;
              to: number;
              count: number;
            }) => t('pagination.displayedRows', { from, to, count }),
          },
        }}
        columnVisibilityModel={context.columnVisibilityModel}
        onColumnVisibilityModelChange={context.onColumnVisibilityModelChange}
        // Pagination-related props
        pagination
        paginationMode='server'
        rowsPerPageOptions={[10, 25, 50]}
        rowCount={context.paginationModel.total}
        page={context.paginationModel.currentPage}
        pageSize={context.paginationModel.pageSize}
        onPageSizeChange={handlePageSizeChange}
        onPageChange={handlePageChange}
        {...rest}
        rows={rows}
      />

      {/* Error overlay that covers only the data area, not the toolbar */}
      {error && (
        <AbsoluteBox hasFilters={hasFilters}>
          <GeneralErrorUI />
        </AbsoluteBox>
      )}
    </Box>
  );
}
